import {updatedEntities, denormalisedEntities} from '../util/data';
import {parse} from '../util/urlHelpers';
import {storableError} from '../util/errors';
import {getBoostersProductCheckoutSessionSecret, getIsABypassMessageInformation} from "../util/api";
import {path} from "ramda";
import {getUserToken} from "./user.duck";

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ownListings/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ownListings/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ownListings/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ownListings/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ownListings/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ownListings/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ownListings/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ownListings/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ownListings/CLOSE_LISTING_ERROR';

export const DISCARD_DRAFT_REQUEST = 'app/ownListings/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/ownListings/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/ownListings/DISCARD_DRAFT_ERROR';

export const ADD_OWN_ENTITIES = 'app/ownListings/ADD_OWN_ENTITIES';

export const FETCH_BOOSTERS_CHECKOUT_SESSION_REQUEST = 'app/ownListings/FETCH_BOOSTERS_CHECKOUT_SESSION_REQUEST';
export const FETCH_BOOSTERS_CHECKOUT_SESSION_SUCCESS = 'app/ownListings/FETCH_BOOSTERS_CHECKOUT_SESSION_SUCCESS';
export const FETCH_BOOSTERS_CHECKOUT_SESSION_ERROR = 'app/ownListings/FETCH_BOOSTERS_CHECKOUT_SESSION_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  discardingDraft: null,
  discardingDraftError: null,
  fetchBoostersCheckoutSessionInProgress: false,
  fetchBoostersCheckoutSessionError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({...state.ownEntities}, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = {...oldListing, attributes: listingEntity.attributes};
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: {...state.ownEntities, ownListing: ownListingEntities},
  };
};

const ownListingsReducer = (state = initialState, action = {}) => {
  const {type, payload} = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {...state, queryInProgress: false, queryListingsError: payload};

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case DISCARD_DRAFT_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        discardingDraft: null,
      };
    case DISCARD_DRAFT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: {
          listingId: state.discardingDraft,
          error: payload,
        },
      };
    }

    case FETCH_BOOSTERS_CHECKOUT_SESSION_REQUEST:
      return {
        ...state,
        fetchBoostersCheckoutSessionInProgress: true,
        fetchBoostersCheckoutSessionError: null,
      };
    case FETCH_BOOSTERS_CHECKOUT_SESSION_SUCCESS:
      return {
        ...state,
        fetchBoostersCheckoutSessionInProgress: false,
        fetchBoostersCheckoutSessionError: null,
      };
    case FETCH_BOOSTERS_CHECKOUT_SESSION_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {...state, fetchBoostersCheckoutSessionInProgress: false, fetchBoostersCheckoutSessionError: payload};

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default ownListingsReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const {ownEntities} = state.ownListings;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: {listingId},
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: {listingId},
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchBoostersCheckoutSessionRequest = () => ({
  type: FETCH_BOOSTERS_CHECKOUT_SESSION_REQUEST,
  payload: {},
});

export const fetchBoostersCheckoutSessionSuccess = response => ({
  type: FETCH_BOOSTERS_CHECKOUT_SESSION_SUCCESS,
  payload: {},
});

export const fetchBoostersCheckoutSessionError = e => ({
  type: FETCH_BOOSTERS_CHECKOUT_SESSION_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: {queryParams},
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: {data: response.data},
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const discardDraftRequest = listingId => ({
  type: DISCARD_DRAFT_REQUEST,
  payload: {listingId},
});

export const discardDraftSuccess = response => ({
  type: DISCARD_DRAFT_SUCCESS,
  payload: response.data,
});

export const discardDraftError = e => ({
  type: DISCARD_DRAFT_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const {perPage, ...rest} = queryParams;
  const params = {...rest, per_page: perPage};

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({id: listingId}, {expand: true})
    .then(response => {
      dispatch(closeListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({id: listingId}, {expand: true})
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const discardDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(discardDraftRequest(listingId));

  return sdk.ownListings
    .discardDraft({id: listingId}, {expand: false})
    .then(response => {
      dispatch(queryOwnListings({
        page: 1,
        perPage: 100,
        include: ['images'],
        'fields.image': ['variants.square-small'],
        'limit.images': 1,
      }));

      dispatch(discardDraftSuccess(response));

      return response;
    })
    .catch(e => {
      dispatch(discardDraftError(storableError(e)));
    });
};

export const createBoostersProductCheckoutSession = (planId) => async (dispatch, getState, sdk) => {
  try {
    dispatch(fetchBoostersCheckoutSessionRequest());
    const token = getUserToken();
    const response = await getBoostersProductCheckoutSessionSecret({planId}, token);
    dispatch(fetchBoostersCheckoutSessionSuccess());
    return path(['data', 'clientSecret'], response);
  } catch (e) {
    dispatch(fetchBoostersCheckoutSessionError(storableError(e)));
  }
};
